import React, {
  type ComponentProps,
  memo,
  type ReactElement,
  type RefObject,
  useCallback,
  useMemo,
} from 'react';
import { type DimensionValue, Platform, StyleSheet, useWindowDimensions, View } from 'react-native';
import { useStyle } from 'react-native-style-utilities';

import { Main } from '@expo/html-elements';
import {
  FlashList,
  type FlashListProps,
  type FlashListRef,
  type ListRenderItemInfo,
} from '@shopify/flash-list';

import { getA11ySelector } from '@sg/a11y-helpers';
import {
  Alert,
  Container,
  HStack,
  theme,
  useContainerMaxWidth,
  useContainerSpacing,
  useResponsive,
} from '@sg/garnish';
import { MenuDietaryPreferences } from '@sg/shared-fe-features-menu';

import { MenuCategoriesGrid } from '@order/components-menu';

import { type useMenuReorderRailOrders } from '../../../../hooks/useMenuReorderRailOrders';
import { AppFooter } from '../../../AppFooter';
import { MenuReorderRail } from '../../../MenuReorderRail';
import {
  useDerivedRestaurantData,
  type useMenuBundleEntryPoint,
  type useMenuContent,
  useMenuGridColumns,
} from '../../hooks';
import {
  type MenuContentDeliveryDetailsData,
  type MenuContentOrderChannel,
  type MenuContentProduct,
  type MenuContentRestaurantData,
} from '../../MenuContent.types';
import { type MenuContentListRow } from '../../utils';
import { MenuContentBundleEntryPointCard } from '../MenuContentBundleEntryPointCard';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Virtualized menu categories. Categories are flattened into rows (heading,
 * one row per product) and rendered by FlashList, so only rows near the
 * viewport are mounted. Products flow through a column grid one card per cell
 * while every other row spans the full width. Every row carries the scrollspy
 * target id of its category so viewability can drive the nav.
 */
export const MenuContentCategories = memo((props: Props) => {
  const {
    ref,
    rows,
    onScroll,
    onLoad,
    onViewableItemsChanged,
    categoriesBadges,
    reorderRailOrders,
    restaurantData,
    orderChannel,
    deliveryDetailsData,
    onDietaryPreferencesTogglePress,
    generateCustomizationHref,
    renderProductCard,
    bundleEntryPoint,
    hasBottomDisclaimer,
  } = props;

  const { match, minWidth } = useResponsive();
  const containerSpacing = useContainerSpacing();
  const containerMaxWidth = useContainerMaxWidth('large');
  const { restaurantId, restaurantName, restaurantSlug, hoursText, shouldRenderHoursMessage } =
    useDerivedRestaurantData({ restaurantData, orderChannel });
  const { width: viewportWidth } = useWindowDimensions();
  const numColumns = useMenuGridColumns();

  const shouldRenderDietaryPreferencesToggle = onDietaryPreferencesTogglePress !== undefined;
  const shouldRenderReorderRailSection = reorderRailOrders.mostRecentLineItems.length > 0;
  const shouldRenderFirstPositionInfoMessages =
    shouldRenderHoursMessage || shouldRenderDietaryPreferencesToggle;

  // ─── Derived ─────────────────────────────────────────────────────────

  const drawDistance = match([DRAW_DISTANCE_XS, DRAW_DISTANCE_SM]);
  const isGrid = numColumns > 1;
  const shouldRenderFooter = minWidth.isSM;

  // ─── Styles ──────────────────────────────────────────────────────────

  // NOTE: In the grid, each cell carries half the column gap on both sides and
  //       the list edge inset shrinks by the same amount, so content still
  //       aligns with the container edge.
  const cellGutter = isGrid ? COLUMN_GAP / 2 : 0;

  const contentContainerDynamicStyles = useStyle(
    () => ({
      paddingHorizontal: containerSpacing - cellGutter,
      paddingBottom: shouldRenderFooter ? 0 : BOTTOM_SPACING,
      ...containerMaxWidth,
    }),
    [containerSpacing, cellGutter, shouldRenderFooter, containerMaxWidth],
  );
  const contentContainerStyles = [styles.contentContainer, contentContainerDynamicStyles];

  // NOTE: The footer is edge to edge, so it cancels the centered max width and
  //       the inset the content container applies to the rows. The list spans
  //       the viewport, which is why its width needs no measuring.
  const contentWidth = resolveContentWidth(viewportWidth, containerMaxWidth.maxWidth);
  const contentInset = (viewportWidth - contentWidth) / 2 + containerSpacing - cellGutter;
  const listFooterDynamicStyles = useStyle(
    () => ({ width: viewportWidth, marginLeft: -contentInset, marginTop: BOTTOM_SPACING }),
    [viewportWidth, contentInset],
  );

  const rowDynamicStyles = useStyle(() => ({ paddingHorizontal: cellGutter }), [cellGutter]);
  const rowStyles = useMemo(() => [styles.row, rowDynamicStyles], [rowDynamicStyles]);

  const productCellDynamicStylesXS = useStyle(
    () => ({ marginHorizontal: -containerSpacing }),
    [containerSpacing],
  );
  const productCellDynamicStyles = match([productCellDynamicStylesXS, undefined]);
  const productCellStyles = useMemo(
    () => [styles.productCell, productCellDynamicStyles],
    [productCellDynamicStyles],
  );

  const reorderRailRowDynamicStyles = useStyle(
    () => ({ marginHorizontal: -containerSpacing }),
    [containerSpacing],
  );

  const infoMessagesResponsiveStyles = match([
    styles.infoMessagesContainerXS,
    styles.infoMessagesContainerSM,
  ]);
  const infoMessagesContainerStyles = useMemo(
    () => [styles.infoMessagesContainer, infoMessagesResponsiveStyles],
    [infoMessagesResponsiveStyles],
  );

  const reorderRailInfoMessagesResponsiveStyles = match([
    styles.reorderRailInfoMessagesContainerXS,
    styles.reorderRailInfoMessagesContainerSM,
  ]);
  const reorderRailInfoMessagesContainerStyles = useMemo(
    () => [styles.infoMessagesContainer, reorderRailInfoMessagesResponsiveStyles],
    [reorderRailInfoMessagesResponsiveStyles],
  );

  // ─── Handlers ────────────────────────────────────────────────────────

  const renderRow = useCallback(
    (row: MenuContentListRow) => {
      //
      // ─── Reorder Rail ────────────────────────────────────────────────

      if (row.kind === 'reorder-rail') {
        return (
          <View style={reorderRailRowDynamicStyles}>
            {shouldRenderFirstPositionInfoMessages ? (
              <Container>
                <View style={reorderRailInfoMessagesContainerStyles}>
                  {shouldRenderHoursMessage ? (
                    <Alert.Container palette="info">
                      <Alert.Text>{hoursText}</Alert.Text>
                    </Alert.Container>
                  ) : null}

                  {shouldRenderDietaryPreferencesToggle ? (
                    <MenuDietaryPreferences.Toggle onPress={onDietaryPreferencesTogglePress} />
                  ) : null}
                </View>
              </Container>
            ) : null}

            <MenuReorderRail
              lineItems={reorderRailOrders.mostRecentLineItems}
              mostRecentOrder={reorderRailOrders.mostRecentOrder}
              restaurantId={restaurantId}
              restaurantName={restaurantName}
              restaurantSlug={restaurantSlug}
              deliveryDetails={deliveryDetailsData}
            />
          </View>
        );
      }

      const { category, categoryIndex } = row;

      const a11ySelector = getA11ySelector('menu.categories-grid.category.container', {
        group: category.name.toLowerCase(),
        index: categoryIndex,
      });

      // ─── Custom Category ─────────────────────────────────────────────

      if (row.kind === 'custom-products') {
        const hasMultipleProducts = row.products.length > 1;
        const itemsPerRow = hasMultipleProducts ? match([1, 1, 2]) : 1;

        return (
          <MenuCategoriesGrid.CategoryRow position="only" {...a11ySelector.props}>
            <HStack itemsPerRow={itemsPerRow} gap={theme.spacing['4']}>
              {row.products.map((product) => (
                <MenuCategoriesGrid.CustomProductCard
                  key={product.id}
                  product={product}
                  generateHref={generateCustomizationHref}
                />
              ))}
            </HStack>
          </MenuCategoriesGrid.CategoryRow>
        );
      }

      // ─── Dense Products ──────────────────────────────────────────────

      if (row.kind === 'dense-product-pair') {
        return (
          <MenuCategoriesGrid.CategoryRow
            position={row.isLastRow ? 'last' : 'middle'}
            {...a11ySelector.props}
          >
            <MenuCategoriesGrid.DenseProductsRow>
              {row.products.map((product) => renderProductCard(product, 'dense'))}
            </MenuCategoriesGrid.DenseProductsRow>
          </MenuCategoriesGrid.CategoryRow>
        );
      }

      // ─── Bundle Entry Point ──────────────────────────────────────────

      if (row.kind === 'bundle-entry-point') {
        if (!bundleEntryPoint) {
          return null;
        }

        return (
          <View style={productCellStyles}>
            <MenuContentBundleEntryPointCard content={bundleEntryPoint.content} />
          </View>
        );
      }

      // ─── Category Heading ────────────────────────────────────────────

      if (row.kind === 'category-heading') {
        const isFirstCategory = categoryIndex === 0;
        const shouldRenderStoreHoursMessage = !shouldRenderReorderRailSection && isFirstCategory && shouldRenderHoursMessage; // prettier-ignore
        const shouldRenderCategoryDietaryPreferencesToggle = !shouldRenderReorderRailSection && isFirstCategory && shouldRenderDietaryPreferencesToggle; // prettier-ignore
        const shouldRenderInfoMessages = shouldRenderStoreHoursMessage || shouldRenderCategoryDietaryPreferencesToggle; // prettier-ignore

        return (
          <MenuCategoriesGrid.CategoryRow position="first" {...a11ySelector.props}>
            {shouldRenderInfoMessages ? (
              <View style={infoMessagesContainerStyles}>
                {shouldRenderStoreHoursMessage ? (
                  <Alert.Container palette="info">
                    <Alert.Text>{hoursText}</Alert.Text>
                  </Alert.Container>
                ) : null}

                {shouldRenderCategoryDietaryPreferencesToggle ? (
                  <MenuDietaryPreferences.Toggle onPress={onDietaryPreferencesTogglePress} />
                ) : null}
              </View>
            ) : null}

            <MenuCategoriesGrid.CategoryHeading badge={categoriesBadges[category.id]}>
              {category.name}
            </MenuCategoriesGrid.CategoryHeading>
          </MenuCategoriesGrid.CategoryRow>
        );
      }

      // ─── Product ─────────────────────────────────────────────────────

      // NOTE: The last product closes the category spacing itself. A separate
      //       footer row would have no height on breakpoints without bottom
      //       spacing, and a zero-height row counts as fully viewable whenever
      //       it sits at the viewport edge, which would steal the active target.
      if (row.isLastProduct) {
        return (
          <MenuCategoriesGrid.CategoryRow position="last" style={productCellStyles}>
            {renderProductCard(row.product)}
          </MenuCategoriesGrid.CategoryRow>
        );
      }

      return <View style={productCellStyles}>{renderProductCard(row.product)}</View>;
    },
    [
      bundleEntryPoint,
      categoriesBadges,
      deliveryDetailsData,
      generateCustomizationHref,
      hoursText,
      infoMessagesContainerStyles,
      match,
      onDietaryPreferencesTogglePress,
      productCellStyles,
      renderProductCard,
      reorderRailInfoMessagesContainerStyles,
      reorderRailOrders.mostRecentLineItems,
      reorderRailOrders.mostRecentOrder,
      reorderRailRowDynamicStyles,
      restaurantId,
      restaurantName,
      restaurantSlug,
      shouldRenderDietaryPreferencesToggle,
      shouldRenderFirstPositionInfoMessages,
      shouldRenderHoursMessage,
      shouldRenderReorderRailSection,
    ],
  );

  const renderItem = useCallback(
    (info: ListRenderItemInfo<MenuContentListRow>) => (
      <View style={rowStyles}>{renderRow(info.item)}</View>
    ),
    [renderRow, rowStyles],
  );

  // ─────────────────────────────────────────────────────────────────────

  return (
    <Main style={styles.main}>
      <FlashList
        ref={ref}
        data={rows}
        keyExtractor={extractRowKey}
        getItemType={getRowType}
        renderItem={renderItem}
        numColumns={numColumns}
        overrideItemLayout={overrideRowLayout}
        onScroll={onScroll}
        scrollEventThrottle={SCROLL_EVENT_THROTTLE}
        drawDistance={drawDistance}
        onLoad={onLoad}
        viewabilityConfig={VIEWABILITY_CONFIG}
        onViewableItemsChanged={onViewableItemsChanged}
        style={styles.list}
        contentContainerStyle={contentContainerStyles}
        ListFooterComponent={
          shouldRenderFooter ? (
            <AppFooter
              style={hasBottomDisclaimer ? styles.footerWithBottomNotices : undefined}
              isLandmark={false}
            />
          ) : undefined
        }
        ListFooterComponentStyle={listFooterDynamicStyles}
        testID="menu-content.categories-container"
      />
    </Main>
  );
});

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Only product and bundle entry point rows occupy a single column; everything
 * else spans the grid.
 */
function overrideRowLayout(
  layout: { span?: number },
  row: MenuContentListRow,
  _index: number,
  maxColumns: number,
) {
  if (row.kind === 'product' || row.kind === 'bundle-entry-point') return;

  layout.span = maxColumns;
}

function resolveContentWidth(viewportWidth: number, maxWidth: DimensionValue | undefined) {
  if (typeof maxWidth !== 'number') return viewportWidth;

  return Math.min(viewportWidth, maxWidth);
}

function extractRowKey(row: MenuContentListRow) {
  return row.key;
}

function getRowType(row: MenuContentListRow) {
  return row.kind;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const SCROLL_EVENT_THROTTLE = 16;

// NOTE: Web renders large amounts of content far more cheaply than native, so
//       it can afford a much larger draw window. That keeps more of the menu in
//       the DOM for find-in-page, assistive tech and scroll-to-category jumps.

const DRAW_DISTANCE_XS = Platform.select({ web: 5000, default: 800 });
const DRAW_DISTANCE_SM = Platform.select({ web: 5000, default: 1200 });

const COLUMN_GAP = theme.spacing['4'];
const BOTTOM_SPACING = theme.spacing['16'];

const VIEWABILITY_CONFIG: FlashListProps<MenuContentListRow>['viewabilityConfig'] = {
  itemVisiblePercentThreshold: 50,
  minimumViewTime: 0,
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  main: {
    flex: 1,
  },
  list: {
    flex: 1,
  },
  contentContainer: {
    width: '100%',
    alignSelf: 'center',
  },
  row: {
    flex: 1,
  },
  productCell: {
    flex: 1,
    marginBottom: COLUMN_GAP,
  },
  footerWithBottomNotices: {
    paddingBottom: theme.spacing['20'],
  },
  infoMessagesContainer: {
    alignItems: 'flex-start',
    gap: theme.spacing['4'],
    marginBottom: theme.spacing['8'],
  },
  infoMessagesContainerXS: {
    marginTop: -theme.spacing['4'],
  },
  infoMessagesContainerSM: {
    marginTop: -theme.spacing['8'],
  },
  reorderRailInfoMessagesContainerXS: {
    marginTop: theme.spacing['2'],
  },
  reorderRailInfoMessagesContainerSM: {
    marginTop: theme.spacing['6'],
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  ref?: RefObject<FlashListRef<MenuContentListRow> | null>;
  rows: readonly MenuContentListRow[];
  onScroll: FlashListProps<MenuContentListRow>['onScroll'];
  onLoad: FlashListProps<MenuContentListRow>['onLoad'];
  onViewableItemsChanged: ViewableItemsChangedHandler;
  categoriesBadges: ReturnType<typeof useMenuContent>['mappedContent']['categoriesBadges'];
  reorderRailOrders: ReturnType<typeof useMenuReorderRailOrders>;
  restaurantData: MenuContentRestaurantData;
  orderChannel: MenuContentOrderChannel;
  deliveryDetailsData: MenuContentDeliveryDetailsData | undefined;
  onDietaryPreferencesTogglePress?: () => void;
  generateCustomizationHref: ComponentProps<
    typeof MenuCategoriesGrid.CustomProductCard
  >['generateHref'];
  renderProductCard: (
    product: MenuContentProduct,
    variant?: MenuContentProductCardVariant,
  ) => ReactElement;
  bundleEntryPoint: ReturnType<typeof useMenuBundleEntryPoint>;
  hasBottomDisclaimer: boolean;
};

export type MenuContentProductCardVariant = 'standard' | 'dense';

type ViewableItemsChangedHandler = (info: {
  viewableItems: readonly { item: MenuContentListRow }[];
}) => void;
